import { useState } from 'react';
import { useList } from '@refinedev/core';
import { Check, ChevronsUpDown } from 'lucide-react';
import type { SegmentProductRef } from '@masters-club/validation';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  type PricingTierOption,
  resolveTierName,
  formatTierPrices,
} from '@/components/shared/pricing-tier-select';
import { type EntityLike, resolveEntityLabel } from './segment-labels';

/**
 * Обобщённое представление продукта из useList. Локализованное название
 * резолвится общим resolveEntityLabel (см. ./segment-labels).
 */
type ProductItem = EntityLike;

interface ProductRefInputProps {
  /** Refine-ресурс продукта: 'courses' | 'books' | 'channel-passes'. */
  resource: string;
  /** Грузить ли селектор тарифа (только для supportsTier полей). */
  supportsTier: boolean;
  value: SegmentProductRef | undefined;
  onChange: (value: SegmentProductRef | undefined) => void;
  disabled?: boolean;
}

/**
 * Двухуровневый выбор для kind=productRef: продукт (курс/книга/канал)
 * и опционально конкретный тариф этого продукта.
 *
 * Тарифы фильтруются по выбранному продукту через PricingTier-связи
 * (courseId / channelPassId / pricingTierId книги). Если supportsTier=false
 * (прогресс по курсу) — показывается только выбор продукта.
 */
export function ProductRefInput({
  resource,
  supportsTier,
  value,
  onChange,
  disabled = false,
}: ProductRefInputProps) {
  const [productOpen, setProductOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [tierOpen, setTierOpen] = useState(false);

  const productList = useList<ProductItem>({
    resource,
    pagination: { pageSize: 200, mode: 'server' },
    queryOptions: { staleTime: 60_000 },
  });

  const products = productList.result?.data ?? [];
  const selectedProduct =
    value !== undefined ? products.find((p) => Number(p.id) === value.targetId) : undefined;

  const filteredProducts = productSearch
    ? products.filter((p) =>
        resolveEntityLabel(p).toLowerCase().includes(productSearch.toLowerCase()) ||
        String(p.id).includes(productSearch),
      )
    : products;

  // Тарифы выбранного продукта. PricingTier связан с продуктом через
  // courseId / channelPassId; для книг тариф один (pricingTierId книги),
  // но единый список pricing-tiers позволяет отфильтровать по продукту.
  const tierList = useList<PricingTierOption>({
    resource: 'pricing-tiers',
    pagination: { pageSize: 200 },
    queryOptions: {
      staleTime: 60_000,
      enabled: supportsTier && value !== undefined,
    },
  });

  const productTiers = (tierList.result?.data ?? []).filter((t) => {
    if (value === undefined) {
      return false;
    }
    if (resource === 'courses') {
      return t.courseId === value.targetId;
    }
    if (resource === 'channel-passes') {
      return t.channelPassId === value.targetId;
    }
    if (resource === 'subscription-plans') {
      return t.subscriptionPlanId === value.targetId;
    }
    return true; // books и прочее — тариф привязан иначе, показываем все
  });

  const selectedTier =
    value?.tierId !== undefined
      ? productTiers.find((t) => t.id === value.tierId)
      : undefined;

  const handleProductSelect = (productId: number) => {
    onChange({ targetId: productId });
    setProductOpen(false);
    setProductSearch('');
  };

  const handleTierSelect = (tierId: number | null) => {
    if (value === undefined) {
      return;
    }
    onChange(
      tierId === null
        ? { targetId: value.targetId }
        : { targetId: value.targetId, tierId },
    );
    setTierOpen(false);
  };

  return (
    <div className="space-y-2">
      {/* Выбор продукта */}
      <Popover open={productOpen} onOpenChange={setProductOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-expanded={productOpen}
            className="w-full justify-between"
            disabled={disabled}
          >
            <span className="truncate">
              {selectedProduct ? resolveEntityLabel(selectedProduct) : 'Выберите продукт...'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Поиск..."
              value={productSearch}
              onValueChange={setProductSearch}
            />
            <CommandList>
              <CommandEmpty>Ничего не найдено</CommandEmpty>
              <CommandGroup>
                {filteredProducts.map((p) => (
                  <CommandItem
                    key={p.id}
                    value={String(p.id)}
                    onSelect={() => handleProductSelect(Number(p.id))}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value?.targetId === Number(p.id) ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="truncate">{resolveEntityLabel(p)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Опциональный выбор тарифа */}
      {supportsTier && value !== undefined && (
        <Popover open={tierOpen} onOpenChange={setTierOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-expanded={tierOpen}
              className="w-full justify-between text-xs"
              disabled={disabled}
            >
              <span className="truncate">
                {selectedTier ? resolveTierName(selectedTier) : 'Любой тариф'}
              </span>
              <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
            <Command shouldFilter={false}>
              <CommandList>
                <CommandEmpty>У продукта нет тарифов</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    key="__any__"
                    value="__any__"
                    onSelect={() => handleTierSelect(null)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value.tierId === undefined ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="text-muted-foreground">Любой тариф</span>
                  </CommandItem>
                  {productTiers.map((tier) => (
                    <CommandItem
                      key={tier.id}
                      value={String(tier.id)}
                      onSelect={() => handleTierSelect(tier.id)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value.tierId === tier.id ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <div className="flex flex-1 items-center justify-between gap-2">
                        <span className="truncate">{resolveTierName(tier)}</span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatTierPrices(tier)}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
